import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { loadStripe } from "@stripe/stripe-js";
import moment from "moment";

import HelpPopover from "./common/Popover";
import UpdateMenu from "./common/UpdateMenu";
import CustomChip from "components/common/Chip";
import CustomButton from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import TopHeader from "components/common/TopHeader";
import RiveAnimation from "components/common/RiveAnimation";
import {
  CardExpiryWarning,
  getPaymentChipIconColor,
  getPaymentChipColor,
  getPaymentChipLabel,
} from "components/Screens/Customers/utils";
import { usePermissions } from "components/Hook";
import { hasPermission } from "hooks/usePermission";

import { getPlatformConfig } from "selectors/platformConfig";
import { getUserAdditionalInfo, getLoadingAdditionalInfo } from "selectors/user";
import { baseUrl } from "util/APIBaseUrl";

import { CustomerBankInfo } from "store/actions/Payment/GetCustomerBankInfo";
import { ResetAddCustomerBankInfo } from "store/actions/Payment/AddBankAccountInfo";
import { showSnackbar } from "store/actions/Utility";
import { AlertType } from "components/common/Snackbar";
import { createFinancialConnectionsSetupIntent, finalizeFinancialConnectionsSetup } from "services/customer";

import {
  HelpOutlineOutlined as HelpIcon,
  AccountBalanceOutlined as AccountBalanceIcon,
  CreditCardOutlined as CreditCardIcon,
  ChevronRightOutlined as ChevronRightIcon,
} from "@mui/icons-material";
import VisaCardIcon from "assets/svgs/VisaCardIcon";
import PaymentWorldMap from "assets/svgs/PaymentWorldMap";
import MasterCardIcon from "assets/svgs/MasterCardIcon";
import AmericanExpressCardIcon from "assets/svgs/AmericanExpressCardIcon";

import "./Payment.css";

function renderPaymentCard(brand) {
  switch (brand) {
    case "amex":
      return <AmericanExpressCardIcon />;
    case "visa":
      return <VisaCardIcon />;
    default:
      return <MasterCardIcon />;
  }
}

function PaymentMethods() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loggedInUserType } = usePermissions();
  const { stripeFc = false } = useFlags();

  const canManageMethod = hasPermission("payments.manage_method");

  const platformConfig = useSelector(getPlatformConfig);
  const userAdditionalInfo = useSelector(getUserAdditionalInfo);
  const loadingUserInfo = useSelector(getLoadingAdditionalInfo);

  const [anchorEl, setAnchorEl] = useState(null);
  const [stripeFcLoading, setStripeFcLoading] = useState(false);

  const openInfo = Boolean(anchorEl);
  const id = openInfo ? "simple-popover" : undefined;

  const { payload: platformPayload = {}, ok: isPlatformConfigLoaded = false } = platformConfig || {};

  const loadingBankAccountInfo = useSelector((globalState) => globalState.GetCustomerBankInfo.loadingBankAccountInfo);
  const loadingplatformConfig = useSelector((globalState) => globalState.PlatformConfig.loadingplatformConfig);
  const bankAccountsDetails = useSelector((state) => state.GetCustomerBankInfo.bankAccountInfo);
  const addBankAccountInfo = useSelector((globalState) => globalState.AddBankAccountInfo.addBankAccountInfo);
  const loadingAddBankAccountInfo = useSelector(
    (globalState) => globalState.AddBankAccountInfo.loadingAddBankAccountInfo
  );

  const isLoading = loadingplatformConfig || !isPlatformConfigLoaded;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAnchor = () => {
    setAnchorEl(null);
  };

  const handleConnectViaStripeFC = useCallback(async () => {
    try {
      if (!platformPayload.STRIPE_PUBLISHABLE_KEY) {
        dispatch(showSnackbar({ dispatch, type: AlertType.error, message: "Stripe configuration is missing." }));
        return;
      }
      setStripeFcLoading(true);
      const stripe = await loadStripe(platformPayload.STRIPE_PUBLISHABLE_KEY);
      const fcResponse = await createFinancialConnectionsSetupIntent(userAdditionalInfo?.customerId);
      const fcClientSecret = fcResponse?.clientSecret || fcResponse?.client_secret;
      if (!fcClientSecret) {
        dispatch(showSnackbar({ dispatch, type: AlertType.error, message: "Failed to initialize Stripe." }));
        setStripeFcLoading(false);
        return;
      }
      setStripeFcLoading(false);
      const currentUser = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
      const { setupIntent, error } = await stripe.collectBankAccountForSetup({
        clientSecret: fcClientSecret,
        params: {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: { name: currentUser?.name || "Customer", email: currentUser?.email || "" },
          },
        },
      });
      if (error) {
        dispatch(showSnackbar({ dispatch, type: AlertType.error, message: error.message || "Failed to connect." }));
        return;
      }
      if (setupIntent && setupIntent.payment_method) {
        setStripeFcLoading(true);
        const result = await finalizeFinancialConnectionsSetup(userAdditionalInfo?.customerId, setupIntent.id);
        if (result?.success) {
          dispatch(showSnackbar({ dispatch, type: AlertType.success, message: "Payment method added!" }));
          setStripeFcLoading(false);
          dispatch(CustomerBankInfo({ request: `${baseUrl}/customer/${userAdditionalInfo?.customerId}/bankInfo` }));
        } else {
          dispatch(showSnackbar({ dispatch, type: AlertType.error, message: "Failed to finalize connection." }));
          setStripeFcLoading(false);
        }
      }
    } catch (err) {
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: err[0]?.description || err?.message || "Something went wrong.",
        })
      );
      setStripeFcLoading(false);
    }
  }, [platformPayload.STRIPE_PUBLISHABLE_KEY, userAdditionalInfo?.customerId, dispatch]);

  const handlePaymentMethodClick = (paymentType, update, change) => {
    if (update) navigate(`/main/payment/add-payment-method?type=${paymentType}&update=true`);
    else if (change) navigate(`/main/payment/add-payment-method?type=${paymentType}&change=true`);
    else navigate(`/main/payment/add-payment-method?type=${paymentType}`);
  };

  useEffect(() => {
    if (!userAdditionalInfo?.customerId) return;

    if (addBankAccountInfo?.ok) {
      dispatch(
        CustomerBankInfo({
          request: `${baseUrl}/customer/${userAdditionalInfo?.customerId}/bankInfo`,
        })
      );
    }
  }, [addBankAccountInfo, dispatch, userAdditionalInfo?.customerId]);

  useEffect(() => {
    if (!userAdditionalInfo?.customerId) return;

    dispatch(ResetAddCustomerBankInfo({}));

    dispatch(
      CustomerBankInfo({
        request: `${baseUrl}/customer/${userAdditionalInfo?.customerId}/bankInfo`,
      })
    );
  }, [userAdditionalInfo?.customerId, dispatch]);

  if (loggedInUserType !== "customer" || !isPlatformConfigLoaded) return null;

  const paymentTypes = {
    ach: true,
    creditCard: userAdditionalInfo?.creditCardSettings?.allowCreditCard,
    achData: bankAccountsDetails?.paymentMethodType === "ACH",
    creditCardData: bankAccountsDetails?.paymentMethodType === "CREDIT_CARD",
  };

  // PRD-2145: the manual-payment card is for skip-payment customers who settle
  // invoices manually (wire/cheque) and have NO saved payment method. If a real
  // PM exists (ACH/card) — even alongside skipPayment=true — show that PM instead
  // of the manual card.
  const hasStoredPaymentMethod = paymentTypes.achData || paymentTypes.creditCardData;
  const isManualPayment = bankAccountsDetails?.skipPayment === true && !hasStoredPaymentMethod;

  const manualPaymentLabel =
    bankAccountsDetails?.paymentMethodType === "WIRE_TRANSFER"
      ? "Wire Transfer"
      : bankAccountsDetails?.paymentMethodType === "CHEQUE"
      ? "Cheque"
      : "Manual";

  return (
    <>
      <Helmet>
        <title>Edge | Payment Methods</title>
      </Helmet>
      {(isLoading ||
        loadingAddBankAccountInfo ||
        loadingBankAccountInfo ||
        loadingplatformConfig ||
        loadingUserInfo ||
        stripeFcLoading) && <PageLoader />}

      <div className="payment-container">
        <TopHeader
          title="Payment method"
          secondaryBtn="Need help?"
          secondaryBtnIcon={<HelpIcon className="button-secondary-icon" />}
          secondaryBtnClick={handleClick}
          secondaryBtnVariant="text"
        />

        <HelpPopover id={id} open={openInfo} anchorEl={anchorEl} onClose={handleCloseAnchor} />

        <div className="payment-content-container">
          {isManualPayment && !loadingUserInfo ? (
            <div className="flex flex-col gap-1 w-full">
              <div className="payment-world-map-container p-1">
                <div className="payment-world-map">
                  <PaymentWorldMap />
                </div>
                <div className="flex flex-col gap-05 bank-account-text">
                  <div className="flex items-center gap-025">
                    <AccountBalanceIcon className="payment-icon" />
                    <p className="para-body-s-medium color-text-body-primary">{manualPaymentLabel}</p>
                  </div>
                  <p className="heading-h2-medium">Manual payment</p>
                  <p className="para-body-s-regular color-text-body-secondary">
                    Your invoices are settled manually. Contact your account manager to make any changes.
                  </p>
                </div>
              </div>
              <div className="border-b" />
              {bankAccountsDetails?.currentPayroll && (
                <div className="flex justify-between items-center">
                  <p className="para-body-m-medium color-text-body-primary">
                    Next payment on {moment(bankAccountsDetails?.currentPayroll?.Payroll_Month).format("MMM D")}
                  </p>
                  <CustomChip
                    size="small"
                    onClick={() => navigate("/main/payroll")}
                    color={getPaymentChipColor(bankAccountsDetails?.currentPayroll?.Status)}
                    label={getPaymentChipLabel(bankAccountsDetails?.currentPayroll?.Status)}
                    endIcon={
                      <ChevronRightIcon
                        className={`${getPaymentChipIconColor(bankAccountsDetails?.currentPayroll?.Status)} payment-chip-icon`}
                      />
                    }
                  />
                </div>
              )}
            </div>
          ) : (paymentTypes?.achData || paymentTypes?.creditCardData) && !loadingUserInfo ? (
            <div className="flex flex-col gap-1 w-full">
              <div className="payment-world-map-container p-1">
                <div className="payment-world-map">
                  <PaymentWorldMap />
                </div>
                {paymentTypes.achData ? (
                  <div className="flex flex-col gap-05 bank-account-text">
                    <div className="flex items-center gap-025">
                      <AccountBalanceIcon className="payment-icon" />
                      <p className="para-body-s-medium color-text-body-primary">ACH</p>
                    </div>
                    <p className="heading-h2-medium">
                      {bankAccountsDetails?.bankName}{" "}
                      <span className="color-text-body-secondary">•••• {bankAccountsDetails?.last4}</span>
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-05 bank-account-text">
                    <div className="flex items-center gap-025">
                      <CreditCardIcon className="payment-icon" />
                      <p className="para-body-s-medium color-text-body-primary">Credit/Debit Card</p>
                    </div>
                    <p className="heading-h2-medium flex items-center capitalize z-1 gap-05">
                      {renderPaymentCard(bankAccountsDetails?.brand)} {bankAccountsDetails?.brand}{" "}
                      <span className="color-text-body-secondary">•••• {bankAccountsDetails?.last4}</span>
                    </p>
                    <CardExpiryWarning
                      expiry={bankAccountsDetails?.expiry}
                      warningDays={platformPayload?.CREDIT_CARD_EXPIRY_WARNING_DAYS}
                    />
                  </div>
                )}
                {canManageMethod && (
                  <div className="update-menu-wrapper">
                    <UpdateMenu
                      isAch={paymentTypes.achData}
                      creditAllowed={paymentTypes?.creditCard}
                      onUpdateClick={() => handlePaymentMethodClick(paymentTypes.achData ? "ach" : "card", true)}
                      onChangePaymentMethodClick={() =>
                        handlePaymentMethodClick(paymentTypes.achData ? "card" : "ach", false, true)
                      }
                    />
                  </div>
                )}
              </div>
              <div className="border-b" />
              <div className="flex justify-between items-center">
                <p className="para-body-m-medium color-text-body-primary">
                  Next payment on {moment(bankAccountsDetails?.currentPayroll?.Payroll_Month).format("MMM D")}
                </p>
                <CustomChip
                  size="small"
                  onClick={() => navigate("/main/payroll")}
                  color={getPaymentChipColor(bankAccountsDetails?.currentPayroll?.Status)}
                  label={getPaymentChipLabel(bankAccountsDetails?.currentPayroll?.Status)}
                  endIcon={
                    <ChevronRightIcon
                      className={`${getPaymentChipIconColor(bankAccountsDetails?.currentPayroll?.Status)} payment-chip-icon`}
                    />
                  }
                />
              </div>
              {canManageMethod && paymentTypes?.creditCard && !paymentTypes?.achData && (
                <div className="change-ach-container mt-1">
                  <div className="flex flex-col gap-025">
                    <p className="heading-h4-semibold">Avoid surcharge with ACH Direct Debit</p>
                    <p className="para-body-m-regular light-grey-text">
                      Changing takes less than a minute, and it’s worth it.
                    </p>
                  </div>
                  <CustomButton label="Change to ACH" onClick={() => handlePaymentMethodClick("ach", false, true)} />
                </div>
              )}
            </div>
          ) : (
            <div>
              <RiveAnimation
                style={{
                  marginLeft: "-20px",
                  marginBottom: "-10px",
                }}
                src="/assets/animations/payment_method.riv"
                width="120px"
                height="132px"
              />
              <div className="heading-h1-semibold">Add payment method</div>
              <p className="para-body-m-regular color-text-heading-secondary mt-05">
                Set up your payment method to keep your operations running smoothly and avoid any interruptions. Your
                details are encrypted and safely stored for future use.
              </p>

              {stripeFc ? (
                <div className="flex flex-col mt-2">
                  {/* ACH Direct Debit Section */}
                  <div className="flex flex-col gap-075 mb-05">
                    <p className="heading-h5-medium color-text-heading-primary">ACH direct debit</p>
                    <div className="border-b" />
                  </div>

                  {canManageMethod && (
                    <div
                      className="payment-option-row cursor-pointer"
                      onClick={handleConnectViaStripeFC}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          handleConnectViaStripeFC();
                        }
                      }}
                    >
                      <div className="payment-option-icon-circle">
                        <AccountBalanceIcon className="payment-option-icon" />
                      </div>
                      <div className="payment-option-content">
                        <div className="payment-option-title-row">
                          <span className="payment-option-title">Connect with Stripe</span>
                          <CustomChip label="Recommended" variant="filled" size="small" />
                        </div>
                        <p className="payment-option-description">
                          Log in to your bank account to connect it as a payment method.
                        </p>
                      </div>
                      <ChevronRightIcon className="payment-option-chevron" />
                    </div>
                  )}

                  {canManageMethod && (
                    <div
                      className="payment-option-row cursor-pointer"
                      onClick={() => handlePaymentMethodClick("ach-form")}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          handlePaymentMethodClick("ach-form");
                        }
                      }}
                    >
                      <div className="payment-option-icon-circle">
                        <AccountBalanceIcon className="payment-option-icon" />
                      </div>
                      <div className="payment-option-content">
                        <span className="payment-option-title">Enter bank details</span>
                        <p className="payment-option-description">Manually enter your bank details.</p>
                      </div>
                      <ChevronRightIcon className="payment-option-chevron" />
                    </div>
                  )}

                  {/* Other Options Section */}
                  {canManageMethod && paymentTypes.creditCard && (
                    <>
                      <div className="flex flex-col gap-075 mt-2 mb-05">
                        <p className="heading-h5-medium color-text-heading-primary">Other options</p>
                        <div className="border-b" />
                      </div>

                      <div
                        className="payment-option-row cursor-pointer"
                        onClick={() => handlePaymentMethodClick("card")}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            handlePaymentMethodClick("card");
                          }
                        }}
                      >
                        <div className="payment-option-icon-circle">
                          <CreditCardIcon className="payment-option-icon" />
                        </div>
                        <div className="payment-option-content">
                          <div className="payment-option-title-row">
                            <span className="payment-option-title">Credit / Debit Card</span>
                            <span className="para-body-s-medium color-text-body-primary">Incl. surcharge</span>
                          </div>
                          <p className="payment-option-description">
                            Pay with any major credit or debit card. No bank account linking required.
                          </p>
                        </div>
                        <ChevronRightIcon className="payment-option-chevron" />
                      </div>
                    </>
                  )}
                  <p className="para-body-s-regular color-text-body-primary mt-1">
                    Your payment information is encrypted and securely shared with Stripe for processing. We never store
                    your card or bank account information.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col border rounded-lg mt-1">
                  {canManageMethod && paymentTypes.ach && (
                    <div
                      className="customer-payment-method-card cursor-pointer"
                      onClick={() => handlePaymentMethodClick("ach")}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          handlePaymentMethodClick("ach");
                        }
                      }}
                    >
                      <div className="flex items-center w-full gap-075">
                        <div className="payment-method-icon">
                          <AccountBalanceIcon className="payment-icon" />
                        </div>
                        <div className="payment-method-info">
                          <div className="payment-method-title">
                            <span className="heading-h5-semibold color-text-heading-primary">ACH Direct Debit</span>
                            <CustomChip label="Recommended" variant="filled" size="small" />
                          </div>
                        </div>
                        <ChevronRightIcon className="payment-method-arrow" />
                      </div>
                    </div>
                  )}
                  {canManageMethod && paymentTypes.ach && paymentTypes.creditCard && <div className="border-b h-1p" />}
                  {canManageMethod && paymentTypes.creditCard && (
                    <div
                      className="customer-payment-method-card-cc cursor-pointer"
                      onClick={() => handlePaymentMethodClick("card")}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          handlePaymentMethodClick("card");
                        }
                      }}
                    >
                      <div className="flex items-center w-full gap-075">
                        <div className="payment-method-icon">
                          <CreditCardIcon className="payment-icon" />
                        </div>
                        <div className="payment-method-info">
                          <div className="payment-method-title">
                            <span className="heading-h5-semibold color-text-heading-primary">Credit/Debit Card</span>
                            <span className="mt-2p para-body-s-medium color-text-body-primary">Incl. surcharge</span>
                          </div>
                        </div>
                        <ChevronRightIcon className="payment-method-arrow" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PaymentMethods;
